import React, { FC, useState } from 'react';
import { Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import Modal, { ModalHeader, ModalFooter } from '@/components/Common/Modal';
import styles from './ConflictModal.module.scss';

let conflictModalController: {
    show: (message: React.ReactNode, onConfirm: () => void, onCancel: () => void) => void;
} | null = null;

/**
 * Shows a confirmation modal when a policy setting conflict is detected
 * Asks the user if they want to proceed by disabling the conflicting setting(s)
 */
export const showConflictModal = (
    message: React.ReactNode,
    onConfirm: () => void,
    onCancel: () => void,
): void => {
    if (conflictModalController) {
        conflictModalController.show(message, onConfirm, onCancel);
    }
};

interface ConflictModalState {
    isOpen: boolean;
    message: React.ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
}

interface ConflictModalContentProps {
    message: React.ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
    closeModal: () => void;
}

/**
 * Conflict modal component with custom Modal styling
 */
const ConflictModalContent: FC<ConflictModalContentProps> = ({
    message,
    onConfirm,
    onCancel,
    closeModal,
}) => {
    const handleConfirm = (): void => {
        onConfirm();
        closeModal();
    };

    const handleCancel = (): void => {
        onCancel();
        closeModal();
    };

    return (
        <>
            <ModalHeader>
                <div className={styles['modal-title']}>
                    <ExclamationCircleOutlined className={styles['warning-icon']} />
                    <span>Setting Conflict</span>
                </div>
            </ModalHeader>
            <div className={styles['conflict-message']}>
                {message}
            </div>
            <ModalFooter>
                <div className={styles['buttons-container']}>
                    <Button data-testid="conflictModal_cancelButton" onClick={handleCancel} className={styles['cancel-button']}>
                        Cancel
                    </Button>
                    <Button data-testid="conflictModal_continueButton" onClick={handleConfirm} type="primary" className={styles['continue-button']}>
                        Continue
                    </Button>
                </div>
            </ModalFooter>
        </>
    );
};

/**
 * Conflict modal manager component
 */
const ConflictModal: FC = () => {
    const [state, setState] = useState<ConflictModalState>({
        isOpen: false,
        message: null,
        onConfirm: () => {},
        onCancel: () => {},
    });

    React.useEffect(() => {
        conflictModalController = {
            show: (message, onConfirm, onCancel) => {
                setState({
                    isOpen: true,
                    message,
                    onConfirm,
                    onCancel,
                });
            },
        };

        return () => {
            conflictModalController = null;
        };
    }, []);

    const closeModal = (): void => {
        setState((prev) => ({ ...prev, isOpen: false }));
    };

    return (
        <Modal
            isOpen={state.isOpen}
            closeModal={closeModal}
            destroyOnClose
            initStyle={{ initWidth: 520 }}
        >
            <ConflictModalContent
                message={state.message}
                onConfirm={state.onConfirm}
                onCancel={state.onCancel}
                closeModal={closeModal}
            />
        </Modal>
    );
};

export default ConflictModal;
